/* EL MOTOR DEL PROBADOR — cuelga una obra en la foto del cliente.
   Cuadros Splash (24 sep 2026).

   Qué hace
   --------
   Recibe la foto de una habitación y la imagen limpia de una obra, y dibuja
   el lienzo colgado en la pared como si estuviera allí: con la perspectiva de
   la cámara, a su tamaño real en centímetros, con el canto de 3 cm, la sombra
   que proyecta sobre la pared, la luz y el tono de ESA habitación y el mismo
   grano que la foto. Es lo que separa "una imagen pegada encima" de "un
   cuadro colgado".

   Por qué WebGL a pelo y no three.js
   ----------------------------------
   Aquí no hay escena: hay una foto, una caja y una sombra. Son tres
   programas pequeños. three.js pesaría 600 KB para usar el 2 % de lo que
   trae; esto son unos 20 KB y no le debe nada a nadie.

   La geometría, en una frase
   --------------------------
   Una cámara estenopeica (foco `f` en píxeles, centro en el medio de la
   foto) y un plano de pared. Las medidas que da el cliente ("el sofá mide
   220 cm", "del sofá al techo hay 165") fijan a qué distancia está esa
   pared: cada una, sola, bastaría; con dos se toma la media, pesando más la
   más fiable. A partir de ahí, cualquier punto de la foto se convierte en
   un punto de la pared en centímetros, y cualquier punto de la pared vuelve
   a la foto. El cuadro es una caja de ancho × alto × fondo apoyada en ese
   plano.

   La luz, en otra
   ---------------
   No se inventa: se mide en la propia foto. Una versión muy desenfocada de
   la foto es el mapa de luz de la pared (más clara donde da la lámpara, más
   oscura en la esquina); el lienzo se multiplica por él. El tono de la luz
   sale de los blancos de la foto, el negro del punto más oscuro y el grano
   del ruido de la cámara. Lo que no se puede medir con fiabilidad —cuánta
   luz exacta le llega— se deja en un control para el cliente.

   API (window.CsSalonMotor)
   -------------------------
     var m = CsSalonMotor.crear(canvas);
     m.foto(imagen)                      → analiza y sube la foto
     m.reglas([{ a, b, cm, peso }])      → las medidas de referencia (px de la
                                           foto); devuelve la escala de cada una
     m.regla(a, b, cm)                   → atajo para una sola medida
     m.pared({ yaw, pitch })             → orientación de la pared (grados)
     m.obra(imagen)                      → la obra limpia
     m.tamano(anchoCm, altoCm)
     m.anclar([u, v]) / m.mover(du, dv)  → dónde va el centro inferior
     m.ajustes({ luz, sombra, despegue, oculta })
     m.render()
     m.contiene([u, v])                  → ¿ese píxel cae dentro del cuadro?
     m.cmEntre(a, b) / m.entre(a, b)     → medir sobre la pared, en cm
     m.detectarTecho(x, yMax)            → la junta pared-techo (px) o null
     m.exportar(marca) → Promise<Blob>
*/
(function () {
  'use strict';

  /* ================================================================ */
  /* Vectores                                                           */
  /* ================================================================ */

  function sum(a, b) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
  function res(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
  function esc(a, s) { return [a[0] * s, a[1] * s, a[2] * s]; }
  function pto(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
  function largo(a) { return Math.sqrt(pto(a, a)); }
  function unit(a) { var l = largo(a) || 1; return esc(a, 1 / l); }
  function rad(g) { return (g * Math.PI) / 180; }

  /* ================================================================ */
  /* Análisis de la foto                                                */
  /* ================================================================ */

  function lumS(r, g, b) { return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255; }
  function lin(v) { return Math.pow(v, 2.2); }

  function lienzoDe(img, ancho) {
    var alto = Math.max(1, Math.round((ancho * (img.naturalHeight || img.height)) / (img.naturalWidth || img.width)));
    var c = document.createElement('canvas');
    c.width = ancho;
    c.height = alto;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, 0, 0, ancho, alto);
    return { c: c, x: x, w: ancho, h: alto, d: x.getImageData(0, 0, ancho, alto).data };
  }

  /* Desenfoque de caja separable, tres pasadas ≈ gaussiano. Sobre un lienzo
     de 64 px de ancho: es el mapa de luz, no una imagen. */
  function desenfocar(d, w, h, r) {
    var tmp = new Float32Array(w * h * 3);
    var src = new Float32Array(w * h * 3);
    for (var i = 0; i < w * h; i++) {
      src[i * 3] = d[i * 4];
      src[i * 3 + 1] = d[i * 4 + 1];
      src[i * 3 + 2] = d[i * 4 + 2];
    }
    function pasada(a, b, horizontal) {
      for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
          var s0 = 0, s1 = 0, s2 = 0, n = 0;
          for (var k = -r; k <= r; k++) {
            var xx = horizontal ? Math.min(w - 1, Math.max(0, x + k)) : x;
            var yy = horizontal ? y : Math.min(h - 1, Math.max(0, y + k));
            var j = (yy * w + xx) * 3;
            s0 += a[j]; s1 += a[j + 1]; s2 += a[j + 2]; n++;
          }
          var o = (y * w + x) * 3;
          b[o] = s0 / n; b[o + 1] = s1 / n; b[o + 2] = s2 / n;
        }
      }
    }
    for (var p = 0; p < 3; p++) {
      pasada(src, tmp, true);
      pasada(tmp, src, false);
    }
    return src;
  }

  function percentil(lista, p) {
    var o = lista.slice().sort(function (a, b) { return a - b; });
    return o[Math.min(o.length - 1, Math.max(0, Math.floor(p * (o.length - 1))))];
  }

  /* k-medias sobre una muestra de píxeles: la paleta del salón. Devuelve
     cada color con cuánto pesa y DÓNDE está más concentrado (para que la
     página pueda enseñar la muestra encima de la foto, en su sitio). */
  function paleta(m, k) {
    var pts = [];
    for (var y = 0; y < m.h; y += 2) {
      for (var x = 0; x < m.w; x += 2) {
        var i = (y * m.w + x) * 4;
        pts.push([m.d[i], m.d[i + 1], m.d[i + 2], x / m.w, y / m.h]);
      }
    }
    var c = [];
    for (var j = 0; j < k; j++) {
      var s = pts[Math.floor(((j + 0.5) / k) * pts.length)];
      c.push([s[0], s[1], s[2]]);
    }
    var asig = new Int32Array(pts.length);
    for (var it = 0; it < 10; it++) {
      var acc = c.map(function () { return [0, 0, 0, 0]; });
      for (var q = 0; q < pts.length; q++) {
        var mejor = 0, dm = 1e12;
        for (var t = 0; t < k; t++) {
          var dr = pts[q][0] - c[t][0], dg = pts[q][1] - c[t][1], db = pts[q][2] - c[t][2];
          var dd = dr * dr * 0.3 + dg * dg * 0.59 + db * db * 0.11;
          if (dd < dm) { dm = dd; mejor = t; }
        }
        asig[q] = mejor;
        acc[mejor][0] += pts[q][0]; acc[mejor][1] += pts[q][1]; acc[mejor][2] += pts[q][2]; acc[mejor][3]++;
      }
      for (var u = 0; u < k; u++) {
        if (acc[u][3]) c[u] = [acc[u][0] / acc[u][3], acc[u][1] / acc[u][3], acc[u][2] / acc[u][3]];
      }
    }
    var out = c.map(function (col, idx) {
      var celdas = {};
      var n = 0;
      for (var q = 0; q < pts.length; q++) {
        if (asig[q] !== idx) continue;
        n++;
        var key = Math.floor(pts[q][3] * 8) + ',' + Math.floor(pts[q][4] * 8);
        celdas[key] = (celdas[key] || 0) + 1;
      }
      var top = null, topN = 0;
      Object.keys(celdas).forEach(function (kk) { if (celdas[kk] > topN) { topN = celdas[kk]; top = kk; } });
      var xy = top ? top.split(',').map(function (v) { return (Number(v) + 0.5) / 8; }) : [0.5, 0.5];
      return {
        rgb: col.map(Math.round),
        hex: '#' + col.map(function (v) { return ('0' + Math.round(v).toString(16)).slice(-2); }).join(''),
        peso: n / pts.length,
        donde: xy,
      };
    });
    /* Fuera los casi repetidos: dos grises que difieren en 6 niveles son el
       mismo gris para quien mira. */
    out.sort(function (a, b) { return b.peso - a.peso; });
    var final = [];
    out.forEach(function (o) {
      var repe = final.some(function (f) {
        var d = Math.abs(f.rgb[0] - o.rgb[0]) + Math.abs(f.rgb[1] - o.rgb[1]) + Math.abs(f.rgb[2] - o.rgb[2]);
        return d < 36;
      });
      if (!repe && o.peso > 0.015) final.push(o);
    });
    return final;
  }

  /* El ruido de la cámara: desviación mediana del residuo (foto menos su
     versión suavizada) en un recorte central a resolución de trabajo. */
  function grano(fuente) {
    var lado = Math.min(220, fuente.width, fuente.height);
    var c = document.createElement('canvas');
    c.width = c.height = lado;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(fuente, (fuente.width - lado) / 2, (fuente.height - lado) / 2, lado, lado, 0, 0, lado, lado);
    var d = x.getImageData(0, 0, lado, lado).data;
    var L = new Float32Array(lado * lado);
    for (var i = 0; i < L.length; i++) L[i] = lumS(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]);
    var resid = [];
    for (var yy = 1; yy < lado - 1; yy += 2) {
      for (var xx = 1; xx < lado - 1; xx += 2) {
        var j = yy * lado + xx;
        var m = (L[j - 1] + L[j + 1] + L[j - lado] + L[j + lado]) / 4;
        resid.push(Math.abs(L[j] - m));
      }
    }
    return 1.4826 * percentil(resid, 0.5);
  }

  function analizar(trabajo) {
    var mini = lienzoDe(trabajo, 256);
    var lums = [];
    for (var i = 0; i < mini.d.length; i += 4) lums.push(lumS(mini.d[i], mini.d[i + 1], mini.d[i + 2]));
    var p01 = percentil(lums, 0.01);
    var p95 = percentil(lums, 0.95);

    /* El tono de la luz: la media de los blancos que no están quemados. */
    var r = 0, g = 0, b = 0, n = 0;
    for (var q = 0; q < mini.d.length; q += 4) {
      var l = lumS(mini.d[q], mini.d[q + 1], mini.d[q + 2]);
      if (l >= p95 * 0.97 && l < 0.985) { r += lin(mini.d[q] / 255); g += lin(mini.d[q + 1] / 255); b += lin(mini.d[q + 2] / 255); n++; }
    }
    var tinte = [1, 1, 1];
    if (n > 20) {
      var lm = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      tinte = [r / lm, g / lm, b / lm].map(function (v) { return Math.max(0.8, Math.min(1.2, v)); });
    }

    /* El mapa de luz: 64 px de ancho, muy desenfocado. */
    var luz = lienzoDe(trabajo, 64);
    var borroso = desenfocar(luz.d, luz.w, luz.h, 4);
    var pix = luz.x.createImageData(luz.w, luz.h);
    for (var t = 0; t < luz.w * luz.h; t++) {
      pix.data[t * 4] = borroso[t * 3];
      pix.data[t * 4 + 1] = borroso[t * 3 + 1];
      pix.data[t * 4 + 2] = borroso[t * 3 + 2];
      pix.data[t * 4 + 3] = 255;
    }
    luz.x.putImageData(pix, 0, 0);

    return {
      negro: Math.min(0.07, p01 * 0.7),
      blanco: p95,
      tinte: tinte,
      grano: Math.max(0.004, Math.min(0.035, grano(trabajo) * 1.1)),
      paleta: paleta(mini, 7),
      luz: { c: luz.c, w: luz.w, h: luz.h, rgb: borroso },
    };
  }

  /* ================================================================ */
  /* Perspectiva: rectas y puntos de fuga                               */
  /* ================================================================ */

  /* Las líneas rectas de la foto (el canto de la tele, el tablero del
     mueble, el marco de una ventana, el rodapié) delatan cómo está girada la
     pared: sus horizontales se juntan en un punto, a un lado, y ese punto
     dice el ángulo. Es la misma cuenta que hace un pintor con la línea de
     horizonte, solo que midiendo.

     El detector es un LSD de andar por casa: píxeles de borde con la misma
     orientación, unidos en regiones; cada región alargada y fina es un
     segmento. Sobre la foto reducida a 640 px, unas decenas de ms. */

  function cruz(a, b) {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  }

  function segmentos(fuente) {
    var lado = 640;
    var s = Math.min(1, lado / Math.max(fuente.width, fuente.height));
    var w = Math.round(fuente.width * s), h = Math.round(fuente.height * s);
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(fuente, 0, 0, w, h);
    var d = x.getImageData(0, 0, w, h).data;
    var g = new Float32Array(w * h);
    for (var i = 0; i < w * h; i++) g[i] = lumS(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]);

    var mag = new Float32Array(w * h), bin = new Int8Array(w * h);
    var NB = 24;
    for (var yy = 1; yy < h - 1; yy++) {
      for (var xx = 1; xx < w - 1; xx++) {
        var k = yy * w + xx;
        var gx = g[k - w + 1] + 2 * g[k + 1] + g[k + w + 1] - g[k - w - 1] - 2 * g[k - 1] - g[k + w - 1];
        var gy = g[k + w - 1] + 2 * g[k + w] + g[k + w + 1] - g[k - w - 1] - 2 * g[k - w] - g[k - w + 1];
        mag[k] = Math.sqrt(gx * gx + gy * gy);
        /* orientación de la RECTA (perpendicular al gradiente), 0-180º */
        var a = Math.atan2(gy, gx) + Math.PI / 2;
        a = ((a % Math.PI) + Math.PI) % Math.PI;
        bin[k] = Math.floor((a / Math.PI) * NB) % NB;
      }
    }
    var umbral = 0.3;
    /* De paso, el mapa de "cosas" de la pared: cuántos píxeles de borde hay
       en cada zona (imagen integral). Una pared libre casi no tiene; una
       tele, una estantería o una ventana, muchos. Sirve para no colgar el
       cuadro encima de nada. */
    var ii = new Float32Array((w + 1) * (h + 1));
    for (var iy = 0; iy < h; iy++) {
      var fila = 0;
      for (var ix = 0; ix < w; ix++) {
        fila += mag[iy * w + ix] >= umbral ? 1 : 0;
        ii[(iy + 1) * (w + 1) + ix + 1] = ii[iy * (w + 1) + ix + 1] + fila;
      }
    }
    var visto = new Uint8Array(w * h);
    var pila = new Int32Array(w * h);
    var out = [];
    for (var inicio = 0; inicio < w * h; inicio++) {
      if (visto[inicio] || mag[inicio] < umbral) continue;
      var b0 = bin[inicio];
      var n = 0, sx = 0, sy = 0, sxx = 0, syy = 0, sxy = 0;
      var arriba = 0;
      pila[arriba++] = inicio;
      visto[inicio] = 1;
      var pts = [];
      while (arriba) {
        var p = pila[--arriba];
        var px = p % w, py = (p - px) / w;
        n++; sx += px; sy += py; sxx += px * px; syy += py * py; sxy += px * py;
        if (pts.length < 4000) pts.push(px, py);
        for (var dy = -1; dy <= 1; dy++) {
          for (var dx = -1; dx <= 1; dx++) {
            var qx = px + dx, qy = py + dy;
            if (qx < 1 || qy < 1 || qx >= w - 1 || qy >= h - 1) continue;
            var q = qy * w + qx;
            if (visto[q] || mag[q] < umbral) continue;
            var db = Math.abs(bin[q] - b0);
            if (Math.min(db, NB - db) > 1) continue;
            visto[q] = 1;
            pila[arriba++] = q;
          }
        }
      }
      if (n < 18) continue;
      var mx = sx / n, my = sy / n;
      var cxx = sxx / n - mx * mx, cyy = syy / n - my * my, cxy = sxy / n - mx * my;
      var tr = cxx + cyy, det = cxx * cyy - cxy * cxy;
      var l1 = tr / 2 + Math.sqrt(Math.max(0, (tr * tr) / 4 - det)), l2 = tr - l1;
      var largoSeg = Math.sqrt(12 * l1);
      if (largoSeg < 22 || l2 > 2.2) continue;
      var ang = 0.5 * Math.atan2(2 * cxy, cxx - cyy);
      var ux = Math.cos(ang), uy = Math.sin(ang);
      var tmin = Infinity, tmax = -Infinity;
      for (var j = 0; j < pts.length; j += 2) {
        var t = (pts[j] - mx) * ux + (pts[j + 1] - my) * uy;
        if (t < tmin) tmin = t;
        if (t > tmax) tmax = t;
      }
      var grados = (ang * 180) / Math.PI; /* -90..90, 0 = horizontal */
      out.push({
        x1: (mx + ux * tmin) / s, y1: (my + uy * tmin) / s,
        x2: (mx + ux * tmax) / s, y2: (my + uy * tmax) / s,
        largo: (tmax - tmin) / s,
        ang: grados,
      });
    }
    return { segs: out, bordes: { w: w, h: h, s: s, ii: ii } };
  }

  /* Fracción de píxeles de borde dentro de un rectángulo (px de la foto). */
  function densidad(b, x0, y0, x1, y1) {
    var W1 = b.w + 1;
    var a0 = Math.max(0, Math.min(b.w, Math.round(x0 * b.s))), a1 = Math.max(0, Math.min(b.w, Math.round(x1 * b.s)));
    var c0 = Math.max(0, Math.min(b.h, Math.round(y0 * b.s))), c1 = Math.max(0, Math.min(b.h, Math.round(y1 * b.s)));
    var area = (a1 - a0) * (c1 - c0);
    if (area <= 0) return 1;
    var n = b.ii[c1 * W1 + a1] - b.ii[c0 * W1 + a1] - b.ii[c1 * W1 + a0] + b.ii[c0 * W1 + a0];
    return n / area;
  }

  /* Punto de fuga de una familia de segmentos: RANSAC sobre parejas de los
     más largos, y se queda el punto que más longitud de recta "apunta" a él.
     En homogéneas, así que un punto en el infinito (rectas paralelas, pared
     de frente) sale igual de bien que uno a la derecha de la foto. */
  function fuga(segs) {
    if (segs.length < 2) return null;
    var lista = segs.slice().sort(function (a, b) { return b.largo - a.largo; }).slice(0, 36);
    var L = lista.map(function (s) {
      var l = cruz([s.x1, s.y1, 1], [s.x2, s.y2, 1]);
      var m = Math.hypot(l[0], l[1]) || 1;
      return [l[0] / m, l[1] / m, l[2] / m];
    });
    function apoyo(v) {
      var total = 0, n = 0, dentro = [];
      var mv = Math.hypot(v[0], v[1], v[2]) || 1;
      for (var k = 0; k < lista.length; k++) {
        var s = lista[k];
        var mx = (s.x1 + s.x2) / 2, my = (s.y1 + s.y2) / 2;
        var dx = v[0] / mv - mx * (v[2] / mv), dy = v[1] / mv - my * (v[2] / mv);
        var sx = s.x2 - s.x1, sy = s.y2 - s.y1;
        var c = Math.abs(dx * sx + dy * sy) / ((Math.hypot(dx, dy) * Math.hypot(sx, sy)) || 1);
        var err = (Math.acos(Math.min(1, c)) * 180) / Math.PI;
        var tol = Math.min(3, Math.max(0.8, 60 / s.largo));
        if (err < tol) { total += s.largo; n++; dentro.push(k); }
      }
      return { total: total, n: n, dentro: dentro };
    }
    var mejor = null;
    for (var i = 0; i < L.length; i++) {
      for (var j = i + 1; j < L.length; j++) {
        var v = cruz(L[i], L[j]);
        if (Math.hypot(v[0], v[1], v[2]) < 1e-9) continue;
        var a = apoyo(v);
        if (!mejor || a.total > mejor.total) mejor = { v: v, total: a.total, n: a.n, dentro: a.dentro };
      }
    }
    if (!mejor) return null;
    /* Afinado: el punto que mejor casa con TODAS las rectas que lo apoyan
       (mínimos cuadrados: autovector menor de la suma de l·lᵀ). */
    var M = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    mejor.dentro.forEach(function (k) {
      var l = L[k], p = lista[k].largo;
      for (var r = 0; r < 3; r++) for (var c = 0; c < 3; c++) M[r][c] += p * l[r] * l[c];
    });
    var v = autovectorMenor(M) || mejor.v;
    return { v: v, apoyo: mejor.total, n: mejor.n };
  }

  /* Autovector del menor autovalor de una 3×3 simétrica (Jacobi). */
  function autovectorMenor(A) {
    var a = A.map(function (f) { return f.slice(); });
    var V = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
    for (var it = 0; it < 30; it++) {
      var p = 0, q = 1;
      if (Math.abs(a[0][2]) > Math.abs(a[p][q])) { p = 0; q = 2; }
      if (Math.abs(a[1][2]) > Math.abs(a[p][q])) { p = 1; q = 2; }
      if (Math.abs(a[p][q]) < 1e-12) break;
      var th = 0.5 * Math.atan2(2 * a[p][q], a[q][q] - a[p][p]);
      var c = Math.cos(th), s = Math.sin(th);
      for (var k = 0; k < 3; k++) {
        var akp = a[k][p], akq = a[k][q];
        a[k][p] = c * akp - s * akq; a[k][q] = s * akp + c * akq;
      }
      for (var k2 = 0; k2 < 3; k2++) {
        var apk = a[p][k2], aqk = a[q][k2];
        a[p][k2] = c * apk - s * aqk; a[q][k2] = s * apk + c * aqk;
      }
      for (var k3 = 0; k3 < 3; k3++) {
        var vkp = V[k3][p], vkq = V[k3][q];
        V[k3][p] = c * vkp - s * vkq; V[k3][q] = s * vkp + c * vkq;
      }
    }
    var i = 0;
    if (a[1][1] < a[i][i]) i = 1;
    if (a[2][2] < a[i][i]) i = 2;
    return [V[0][i], V[1][i], V[2][i]];
  }

  /* ================================================================ */
  /* Programas WebGL                                                    */
  /* ================================================================ */

  var VS_FONDO = '#version 300 es\n' +
    'const vec2 P[3] = vec2[3](vec2(-1.,-1.), vec2(3.,-1.), vec2(-1.,3.));\n' +
    'out vec2 vUv;\n' +
    'void main(){ vec2 p = P[gl_VertexID]; vUv = vec2(p.x*.5+.5, .5-p.y*.5); gl_Position = vec4(p,0.,1.); }';

  var FS_FONDO = '#version 300 es\nprecision highp float;\n' +
    'in vec2 vUv; uniform sampler2D uFoto; out vec4 o;\n' +
    'void main(){ o = vec4(texture(uFoto, vUv).rgb, 1.); }';

  /* Cámara: posición en cm, coordenadas de cámara con y hacia abajo y z
     hacia delante (las de la foto). La proyección se escribe a mano para
     que el foco y el centro salgan exactamente de la foto. */
  var VS_CAMARA = '' +
    'uniform vec4 uK;\n' + /* f, cx, cy, (sin uso) */
    'uniform vec2 uTam;\n' + /* ancho y alto de la foto en px */
    'vec4 proyectar(vec3 X){\n' +
    '  float n = 5., fa = 20000.;\n' +
    '  return vec4(2.*uK.x/uTam.x*X.x + (2.*uK.y/uTam.x - 1.)*X.z,\n' +
    '              -2.*uK.x/uTam.y*X.y + (1. - 2.*uK.z/uTam.y)*X.z,\n' +
    '              (fa+n)/(fa-n)*X.z - 2.*fa*n/(fa-n),\n' +
    '              X.z);\n' +
    '}\n';

  var VS_SOMBRA = '#version 300 es\n' +
    'in vec3 aPos; in vec2 aPared;\n' + VS_CAMARA +
    'out vec2 vPared;\n' +
    'void main(){ vPared = aPared; gl_Position = proyectar(aPos); }';

  /* Sombra de una caja sobre la pared, analítica: el producto de dos
     funciones error da el borde suave de un rectángulo desenfocado. Dos
     capas: la proyectada (desplazada según la luz, amplia) y la de contacto
     (pegada al canto, estrecha), que es la que "apoya" el cuadro. */
  var FS_SOMBRA = '#version 300 es\nprecision highp float;\n' +
    'in vec2 vPared; out vec4 o;\n' +
    'uniform vec2 uMedio;\n' + /* medio ancho y medio alto en cm */
    'uniform vec2 uDesv; uniform float uRadio, uFuerza;\n' +
    'float erfa(float x){ float a=.147; float x2=x*x; return sign(x)*sqrt(1.-exp(-x2*(1.2732+a*x2)/(1.+a*x2))); }\n' +
    'float caja(vec2 p, vec2 m, float r){\n' +
    '  vec2 s = .5*(vec2(erfa((p.x+m.x)/r), erfa((p.y+m.y)/r)) - vec2(erfa((p.x-m.x)/r), erfa((p.y-m.y)/r)));\n' +
    '  return s.x*s.y;\n' +
    '}\n' +
    'void main(){\n' +
    '  float proy = caja(vPared - uDesv, uMedio, uRadio);\n' +
    '  float cont = caja(vPared - uDesv*.25, uMedio + .15, .9);\n' +
    '  float a = uFuerza*(.5*proy + .32*cont);\n' +
    '  o = vec4(vec3(1. - clamp(a,0.,.85)), 1.);\n' +
    '}';

  var VS_OBRA = '#version 300 es\n' +
    'in vec3 aPos; in vec2 aUv; in float aCara; in vec2 aPared;\n' + VS_CAMARA +
    'out vec2 vUv; out float vCara; out vec2 vPared;\n' +
    'void main(){ vUv = aUv; vCara = aCara; vPared = aPared; gl_Position = proyectar(aPos); }';

  var FS_OBRA = '#version 300 es\nprecision highp float;\n' +
    'in vec2 vUv; in float vCara; in vec2 vPared; out vec4 o;\n' +
    'uniform sampler2D uObra, uLuz;\n' +
    'uniform vec2 uTam;\n' +
    'uniform vec4 uRecorte;\n' + /* escala uv y desplazamiento (recorte centrado) */
    'uniform float uExpo, uNegro, uGrano, uRef, uPxCm, uSemilla;\n' +
    'uniform vec3 uTinte; uniform vec2 uLuzDir;\n' +
    'float lum(vec3 c){ return dot(c, vec3(.2126,.7152,.0722)); }\n' +
    'float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898,78.233)) + uSemilla)*43758.5453); }\n' +
    'void main(){\n' +
    '  vec2 uv = vUv*uRecorte.xy + uRecorte.zw;\n' +
    '  vec3 alb = pow(texture(uObra, uv, .35).rgb, vec3(2.2));\n' +
    '  vec2 sp = vec2(gl_FragCoord.x/uTam.x, 1. - gl_FragCoord.y/uTam.y);\n' +
    '  float campo = lum(pow(texture(uLuz, sp).rgb, vec3(2.2))) / uRef;\n' +
    '  float L = mix(1., clamp(campo, .62, 1.38), .85);\n' +
    '  float cara = 1.;\n' +
    '  int k = int(vCara + .5);\n' +
    '  vec2 nrm = k==1 ? vec2(-1.,0.) : k==2 ? vec2(1.,0.) : k==3 ? vec2(0.,-1.) : vec2(0.,1.);\n' +
    '  if (k > 0) {\n' +
    '    cara = .56 + .3*max(0., dot(nrm, uLuzDir));\n' +
    '    if (k==3) cara += .12; if (k==4) cara -= .1;\n' +
    '  }\n' +
    /* La trama del lienzo: solo cuando hay píxeles de sobra para verla (si
       no, sería un muaré). Unos 9 hilos por centímetro. */
    '  float trama = 0.;\n' +
    '  if (k == 0 && uPxCm > 9.) {\n' +
    '    vec2 h = vPared*9.*3.14159;\n' +
    '    trama = (sin(h.x)*sin(h.y*1.03))*.035*smoothstep(9., 22., uPxCm);\n' +
    '  }\n' +
    '  vec3 c = alb*uExpo*L*uTinte*cara*(1. + trama);\n' +
    '  c = pow(max(c, 0.), vec3(1./2.2));\n' +
    '  c = uNegro + c*(1. - uNegro);\n' +
    '  c += (hash(gl_FragCoord.xy) - .5)*uGrano;\n' +
    '  o = vec4(c, 1.);\n' +
    '}';

  function compilar(gl, vs, fs) {
    function sh(tipo, src) {
      var s = gl.createShader(tipo);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    var p = gl.createProgram();
    gl.attachShader(p, sh(gl.VERTEX_SHADER, vs));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    var u = {};
    var n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) {
      var info = gl.getActiveUniform(p, i);
      u[info.name] = gl.getUniformLocation(p, info.name);
    }
    return { p: p, u: u };
  }

  function textura(gl, fuente, mipmaps) {
    var t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, fuente);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    if (mipmaps) {
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      var an = gl.getExtension('EXT_texture_filter_anisotropic');
      if (an) gl.texParameterf(gl.TEXTURE_2D, an.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, gl.getParameter(an.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
    } else {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
    return t;
  }

  /* ================================================================ */
  /* El motor                                                           */
  /* ================================================================ */

  function crear(canvas) {
    var gl = null;
    try {
      gl = canvas.getContext('webgl2', { antialias: true, alpha: false, premultipliedAlpha: false, preserveDrawingBuffer: false });
    } catch (e) { gl = null; }

    var E = {
      W: 0, H: 0, f: 1,
      yaw: 0, pitch: 0, roll: 0,
      segmentos: null,
      reglas: [],       /* [{ a, b, cm, peso }]: las medidas del cliente */
      k: -300,
      ancla: null,      /* punto de la pared (cm, cámara): centro del borde inferior */
      w: 100, h: 70, fondo: 3,
      luz: 1, sombra: 1, despegue: 0, oculta: false,
      analisis: null,
      obraImg: null, obraAspecto: 1,
      luzDir: [0, -1],
    };

    var P = {}, tex = {}, buf = {};
    var semilla = Math.random() * 10;

    if (gl) {
      P.fondo = compilar(gl, VS_FONDO, FS_FONDO);
      P.sombra = compilar(gl, VS_SOMBRA, FS_SOMBRA);
      P.obra = compilar(gl, VS_OBRA, FS_OBRA);
      buf.vaoVacio = gl.createVertexArray();
      buf.sombra = gl.createBuffer();
      buf.obra = gl.createBuffer();
      buf.idx = gl.createBuffer();
      buf.vaoSombra = gl.createVertexArray();
      buf.vaoObra = gl.createVertexArray();

      gl.bindVertexArray(buf.vaoSombra);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.sombra);
      var aP = gl.getAttribLocation(P.sombra.p, 'aPos'), aW = gl.getAttribLocation(P.sombra.p, 'aPared');
      gl.enableVertexAttribArray(aP); gl.vertexAttribPointer(aP, 3, gl.FLOAT, false, 20, 0);
      gl.enableVertexAttribArray(aW); gl.vertexAttribPointer(aW, 2, gl.FLOAT, false, 20, 12);

      gl.bindVertexArray(buf.vaoObra);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.obra);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buf.idx);
      var o = P.obra.p;
      var a1 = gl.getAttribLocation(o, 'aPos'), a2 = gl.getAttribLocation(o, 'aUv'),
        a3 = gl.getAttribLocation(o, 'aCara'), a4 = gl.getAttribLocation(o, 'aPared');
      gl.enableVertexAttribArray(a1); gl.vertexAttribPointer(a1, 3, gl.FLOAT, false, 32, 0);
      gl.enableVertexAttribArray(a2); gl.vertexAttribPointer(a2, 2, gl.FLOAT, false, 32, 12);
      gl.enableVertexAttribArray(a3); gl.vertexAttribPointer(a3, 1, gl.FLOAT, false, 32, 20);
      gl.enableVertexAttribArray(a4); gl.vertexAttribPointer(a4, 2, gl.FLOAT, false, 32, 24);
      /* 5 caras × 2 triángulos */
      var ind = [];
      for (var c = 0; c < 5; c++) ind.push(c * 4, c * 4 + 1, c * 4 + 2, c * 4, c * 4 + 2, c * 4 + 3);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(ind), gl.STATIC_DRAW);
      gl.bindVertexArray(null);
    }

    var ctx2d = gl ? null : canvas.getContext('2d');

    /* ---------- Cámara y pared ---------- */

    function rayo(p) { return [(p[0] - E.W / 2) / E.f, (p[1] - E.H / 2) / E.f, 1]; }
    function proyectar(X) { return [(E.f * X[0]) / X[2] + E.W / 2, (E.f * X[1]) / X[2] + E.H / 2]; }

    /* Orientación de la pared respecto a la cámara: giro de la pared (yaw),
       inclinación de la cámara (pitch) y ladeo de la foto (roll), por ese
       orden. `orientar` hace la cuenta al revés. */
    function ejes() {
      var y = rad(E.yaw), p = rad(E.pitch), r = rad(E.roll);
      var cp = Math.cos(p), sp = Math.sin(p), cr = Math.cos(r), sr = Math.sin(r);
      function R(v) {
        var a = [v[0], cp * v[1] - sp * v[2], sp * v[1] + cp * v[2]];
        return [cr * a[0] - sr * a[1], sr * a[0] + cr * a[1], a[2]];
      }
      return {
        n: R([Math.sin(y), 0, -Math.cos(y)]),   /* normal, hacia la cámara */
        e1: R([Math.cos(y), 0, Math.sin(y)]),   /* horizontal de la pared */
        e2: R([0, 1, 0]),                       /* vertical, hacia abajo */
      };
    }

    /* De los dos puntos de fuga (homogéneos, px de la foto) a yaw/pitch/roll.
       vx: donde se juntan las horizontales de la pared; vy: las verticales.
       Cualquiera de los dos puede faltar (null): se supone recto. */
    function orientar(vx, vy) {
      function dir(v) {
        return unit([(v[0] - (E.W / 2) * v[2]) / E.f, (v[1] - (E.H / 2) * v[2]) / E.f, v[2]]);
      }
      var e1 = vx ? dir(vx) : [1, 0, 0];
      if (e1[0] < 0) e1 = esc(e1, -1);
      var e2 = vy ? dir(vy) : [0, 1, 0];
      if (e2[1] < 0) e2 = esc(e2, -1);
      if (!vy) {
        /* Sin verticales fiables: la vertical es la de la cámara, pero
           perpendicular a la horizontal medida. */
        e2 = unit(res([0, 1, 0], esc(e1, e1[1])));
      }
      e2 = unit(res(e2, esc(e1, pto(e1, e2))));
      var pitch = Math.asin(Math.max(-1, Math.min(1, e2[2])));
      var roll = Math.atan2(-e2[0], e2[1]);
      var cr = Math.cos(roll), sr = Math.sin(roll), cp = Math.cos(pitch), sp = Math.sin(pitch);
      var a = [cr * e1[0] + sr * e1[1], -sr * e1[0] + cr * e1[1], e1[2]];
      var b = [a[0], cp * a[1] + sp * a[2], -sp * a[1] + cp * a[2]];
      var yaw = Math.atan2(b[2], b[0]);
      var g = { yaw: (yaw * 180) / Math.PI, pitch: (pitch * 180) / Math.PI, roll: (roll * 180) / Math.PI };
      /* Fuera de lo creíble para una foto de una pared, no se aplica. */
      if (Math.abs(g.yaw) > 60 || Math.abs(g.pitch) > 25 || Math.abs(g.roll) > 12) return null;
      return g;
    }

    function enPared(p, k, n) {
      var d = rayo(p);
      var den = pto(n, d);
      if (den > -1e-4) return null; /* el rayo no llega a la pared */
      return esc(d, k / den);
    }

    /* Las medidas fijan la distancia. Llevado a una pared a distancia 1, el
       tramo de una medida mide `l`; como todo es lineal en k, esa medida
       sola pondría la pared a cm / l. Es su "escala". */
    function escalaDe(r, n) {
      var a = enPared(r.a, -1, n), b = enPared(r.b, -1, n);
      if (!a || !b) return null;
      var l = largo(res(a, b));
      return l > 1e-6 && r.cm > 0 ? r.cm / l : null;
    }

    /* Con varias, la media geométrica de sus escalas, pesada: una medida
       que se aparta un 10 % tira igual hacia arriba que hacia abajo, y la
       más fiable (la línea más larga, el número que el cliente sabe) manda
       más. Los pesos los pone quien llama. */
    function calibrar() {
      var n = ejes().n;
      var suma = 0, pesos = 0;
      E.reglas.forEach(function (r) {
        var s = escalaDe(r, n);
        var p = r.peso == null ? 1 : r.peso;
        if (!s || !(p > 0)) return;
        suma += p * Math.log(s);
        pesos += p;
      });
      if (pesos > 0) E.k = -Math.exp(suma / pesos);
    }

    function geometria() {
      var ej = ejes();
      var A = E.ancla;
      /* Despegue: el cuadro llega separado de la pared y un poco más alto, y
         se asienta. Es la animación de "colgar". */
      var lev = E.despegue;
      var C = sum(sum(A, esc(ej.e2, -E.h / 2 - lev * 6)), esc(ej.n, lev * 10));
      return { ej: ej, C: C };
    }

    function pxPorCm() {
      if (!E.ancla) return 1;
      var ej = ejes();
      var a = proyectar(E.ancla), b = proyectar(sum(E.ancla, ej.e1));
      return Math.hypot(a[0] - b[0], a[1] - b[1]);
    }

    function campoEn(p) {
      var L = E.analisis && E.analisis.luz;
      if (!L) return 0.5;
      var x = Math.max(0, Math.min(L.w - 1, Math.round((p[0] / E.W) * L.w)));
      var y = Math.max(0, Math.min(L.h - 1, Math.round((p[1] / E.H) * L.h)));
      var j = (y * L.w + x) * 3;
      return 0.2126 * lin(L.rgb[j] / 255) + 0.7152 * lin(L.rgb[j + 1] / 255) + 0.0722 * lin(L.rgb[j + 2] / 255);
    }

    /* Luz de referencia y dirección, medidas alrededor del cuadro: a su
       izquierda y derecha, arriba y abajo. La sombra cae hacia el lado
       oscuro. Con poca diferencia manda la luz de techo (sombra hacia abajo). */
    var previa = [0, -1];
    function medirLuz() {
      if (!E.ancla) return 1;
      var g = geometria(), ej = g.ej, C = g.C;
      var m = function (X) { return campoEn(proyectar(X)); };
      var izq = m(sum(C, esc(ej.e1, -E.w * 0.75))), der = m(sum(C, esc(ej.e1, E.w * 0.75)));
      var arr = m(sum(C, esc(ej.e2, -E.h * 0.75))), aba = m(sum(C, esc(ej.e2, E.h * 0.75)));
      var ref = (izq + der + arr + aba) / 4 || 0.5;
      var gx = (der - izq) / ref, gy = (aba - arr) / ref;
      var fuerza = Math.min(1, Math.hypot(gx, gy) * 2.5);
      var dx = gx * fuerza + previa[0] * 0.6, dy = gy * fuerza + previa[1] * 0.6;
      var l = Math.hypot(dx, dy) || 1;
      E.luzDir = [dx / l, dy / l];
      return ref;
    }

    function exposicion(ref) {
      var A = E.analisis;
      if (!A) return 0.85 * E.luz;
      /* Si la pared es clara, ella misma es la referencia de blanco (una
         pintura clara devuelve ~82 %). Si es oscura o de color, mandan los
         blancos de la foto. El resto lo afina el cliente con el control. */
      var e = Math.max(ref / 0.82, lin(A.blanco) * 0.9);
      return Math.max(0.22, Math.min(1.05, e)) * E.luz;
    }

    /* ---------- Mallas ---------- */

    function mallaObra() {
      var g = geometria(), ej = g.ej, C = g.C;
      var w = E.w / 2, h = E.h / 2, D = E.fondo;
      var fu = Math.min(0.08, D / E.w), fv = Math.min(0.08, D / E.h);
      function P3(s, t, z) { return sum(sum(sum(C, esc(ej.e1, s)), esc(ej.e2, t)), esc(ej.n, z)); }
      var v = [];
      function vert(s, t, z, u, vv, cara) {
        var X = P3(s, t, z);
        v.push(X[0], X[1], X[2], u, vv, cara, s, t);
      }
      /* frente */
      vert(-w, -h, D, 0, 0, 0); vert(w, -h, D, 1, 0, 0); vert(w, h, D, 1, 1, 0); vert(-w, h, D, 0, 1, 0);
      /* izquierda: la franja del borde, en espejo (lienzo envuelto) */
      vert(-w, -h, 0, fu, 0, 1); vert(-w, -h, D, 0, 0, 1); vert(-w, h, D, 0, 1, 1); vert(-w, h, 0, fu, 1, 1);
      /* derecha */
      vert(w, -h, D, 1, 0, 2); vert(w, -h, 0, 1 - fu, 0, 2); vert(w, h, 0, 1 - fu, 1, 2); vert(w, h, D, 1, 1, 2);
      /* arriba */
      vert(-w, -h, 0, 0, fv, 3); vert(w, -h, 0, 1, fv, 3); vert(w, -h, D, 1, 0, 3); vert(-w, -h, D, 0, 0, 3);
      /* abajo */
      vert(-w, h, D, 0, 1, 4); vert(w, h, D, 1, 1, 4); vert(w, h, 0, 1, 1 - fv, 4); vert(-w, h, 0, 0, 1 - fv, 4);
      return new Float32Array(v);
    }

    function parametrosSombra() {
      var lev = E.despegue;
      var D = E.fondo + lev * 10;
      var des = [-E.luzDir[0] * D * 0.75, -E.luzDir[1] * D * 0.75 + D * 0.25];
      return { des: des, radio: 1.1 + D * 0.55 + lev * 4, fuerza: 0.95 * E.sombra * (1 - lev * 0.45) };
    }

    function mallaSombra(ps) {
      var ej = ejes();
      var C = sum(E.ancla, esc(ej.e2, -E.h / 2));
      var m = ps.radio * 3 + 2;
      var s0 = -E.w / 2 - m + Math.min(0, ps.des[0]), s1 = E.w / 2 + m + Math.max(0, ps.des[0]);
      var t0 = -E.h / 2 - m + Math.min(0, ps.des[1]), t1 = E.h / 2 + m + Math.max(0, ps.des[1]);
      var v = [];
      [[s0, t0], [s1, t0], [s1, t1], [s0, t0], [s1, t1], [s0, t1]].forEach(function (q) {
        var X = sum(sum(sum(C, esc(ej.e1, q[0])), esc(ej.e2, q[1])), esc(ej.n, 0.05));
        v.push(X[0], X[1], X[2], q[0], q[1]);
      });
      return new Float32Array(v);
    }

    /* ---------- Dibujo ---------- */

    function recorte() {
      /* La obra limpia no siempre tiene la proporción exacta de la talla: se
         recorta centrada, como se hace al montar el lienzo. */
      var a = E.w / E.h, b = E.obraAspecto;
      if (a > b) { var sy = b / a; return [1, sy, 0, (1 - sy) / 2]; }
      var sx = a / b; return [sx, 1, (1 - sx) / 2, 0];
    }

    function render() {
      if (!E.W) return;
      if (!gl) return render2d();
      gl.viewport(0, 0, E.W, E.H);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.BLEND);

      gl.useProgram(P.fondo.p);
      gl.bindVertexArray(buf.vaoVacio);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex.foto);
      gl.uniform1i(P.fondo.u.uFoto, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (!E.ancla || !tex.obra || E.oculta) return;

      var ref = medirLuz();
      var ps = parametrosSombra();
      var K = [E.f, E.W / 2, E.H / 2, 0];

      /* Sombra: multiplicada sobre la foto. */
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.DST_COLOR, gl.ZERO);
      gl.useProgram(P.sombra.p);
      gl.bindVertexArray(buf.vaoSombra);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.sombra);
      gl.bufferData(gl.ARRAY_BUFFER, mallaSombra(ps), gl.DYNAMIC_DRAW);
      gl.uniform4fv(P.sombra.u.uK, K);
      gl.uniform2f(P.sombra.u.uTam, E.W, E.H);
      gl.uniform2f(P.sombra.u.uMedio, E.w / 2, E.h / 2);
      gl.uniform2f(P.sombra.u.uDesv, ps.des[0], ps.des[1]);
      gl.uniform1f(P.sombra.u.uRadio, ps.radio);
      gl.uniform1f(P.sombra.u.uFuerza, ps.fuerza);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disable(gl.BLEND);

      /* La caja. */
      gl.enable(gl.DEPTH_TEST);
      gl.clear(gl.DEPTH_BUFFER_BIT);
      gl.useProgram(P.obra.p);
      gl.bindVertexArray(buf.vaoObra);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.obra);
      gl.bufferData(gl.ARRAY_BUFFER, mallaObra(), gl.DYNAMIC_DRAW);
      var A = E.analisis;
      var u = P.obra.u;
      gl.uniform4fv(u.uK, K);
      gl.uniform2f(u.uTam, E.W, E.H);
      gl.uniform4fv(u.uRecorte, recorte());
      gl.uniform1f(u.uExpo, exposicion(ref));
      gl.uniform1f(u.uNegro, A ? A.negro : 0.02);
      gl.uniform1f(u.uGrano, A ? A.grano : 0.01);
      gl.uniform1f(u.uRef, ref);
      gl.uniform1f(u.uPxCm, pxPorCm());
      gl.uniform1f(u.uSemilla, semilla);
      gl.uniform3fv(u.uTinte, A ? A.tinte.map(function (t) { return 1 + (t - 1) * 0.85; }) : [1, 1, 1]);
      gl.uniform2fv(u.uLuzDir, E.luzDir);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex.obra);
      gl.uniform1i(u.uObra, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, tex.luz);
      gl.uniform1i(u.uLuz, 1);
      gl.drawElements(gl.TRIANGLES, 30, gl.UNSIGNED_SHORT, 0);
      gl.bindVertexArray(null);
    }

    /* Sin WebGL2 (navegadores muy viejos): la foto y el cuadro plano, con
       sombra de canvas. Menos real, pero el cliente sigue pudiendo decidir. */
    function render2d() {
      var x = ctx2d;
      x.drawImage(E.fotoCanvas, 0, 0);
      if (!E.ancla || !E.obraImg || E.oculta) return;
      var q = esquinas();
      var minX = Math.min(q[0][0], q[3][0]), maxX = Math.max(q[1][0], q[2][0]);
      var minY = Math.min(q[0][1], q[1][1]), maxY = Math.max(q[2][1], q[3][1]);
      x.save();
      x.shadowColor = 'rgba(0,0,0,' + 0.35 * E.sombra + ')';
      x.shadowBlur = 18;
      x.shadowOffsetY = 8;
      var r = recorte();
      var iw = E.obraImg.naturalWidth, ih = E.obraImg.naturalHeight;
      x.drawImage(E.obraImg, r[2] * iw, r[3] * ih, r[0] * iw, r[1] * ih, minX, minY, maxX - minX, maxY - minY);
      x.restore();
    }

    function esquinas() {
      var g = geometria(), ej = g.ej, C = g.C;
      var w = E.w / 2, h = E.h / 2, D = E.fondo;
      return [[-w, -h], [w, -h], [w, h], [-w, h]].map(function (q) {
        return proyectar(sum(sum(sum(C, esc(ej.e1, q[0])), esc(ej.e2, q[1])), esc(ej.n, D)));
      });
    }

    function prepararBordes() {
      if (E.segmentos || !E.fotoCanvas) return;
      var r = segmentos(E.fotoCanvas);
      E.segmentos = r.segs;
      E.bordes = r.bordes;
    }

    /* ¿Cuánto "hay" detrás del cuadro? Densidad de bordes en la caja que
       ocupa en la foto, un poco encogida para no contar su propio canto. */
    function ocupacion() {
      prepararBordes();
      var q = esquinas();
      var xs = q.map(function (p) { return p[0]; }), ys = q.map(function (p) { return p[1]; });
      var x0 = Math.min.apply(null, xs), x1 = Math.max.apply(null, xs);
      var y0 = Math.min.apply(null, ys), y1 = Math.max.apply(null, ys);
      var mx = (x1 - x0) * 0.04, my = (y1 - y0) * 0.04;
      return densidad(E.bordes, x0 + mx, y0 + my, x1 - mx, y1 - my);
    }

    /* ---------- API ---------- */

    var api = {
      webgl: !!gl,

      foto: function (img) {
        var lado = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height);
        var tope = Math.min(1800, (gl && gl.getParameter(gl.MAX_TEXTURE_SIZE)) || 4096);
        var s = Math.min(1, tope / lado);
        E.W = Math.round((img.naturalWidth || img.width) * s);
        E.H = Math.round((img.naturalHeight || img.height) * s);
        canvas.width = E.W;
        canvas.height = E.H;
        var fc = document.createElement('canvas');
        fc.width = E.W; fc.height = E.H;
        fc.getContext('2d').drawImage(img, 0, 0, E.W, E.H);
        E.fotoCanvas = fc;
        /* Foco de un móvil (26 mm equivalentes): 0,6 × diagonal. Cambia poco
           el resultado y no hay forma fiable de leerlo de una foto sin EXIF. */
        E.f = 0.6 * Math.hypot(E.W, E.H);
        E.analisis = analizar(fc);
        E.segmentos = null;
        if (gl) {
          if (tex.foto) gl.deleteTexture(tex.foto);
          if (tex.luz) gl.deleteTexture(tex.luz);
          tex.foto = textura(gl, fc, false);
          tex.luz = textura(gl, E.analisis.luz.c, false);
        }
        E.ancla = null;
        return { W: E.W, H: E.H, paleta: E.analisis.paleta };
      },

      tamanoFoto: function () { return [E.W, E.H]; },

      regla: function (a, b, cm) { return api.reglas([{ a: a, b: b, cm: cm, peso: 1 }]); },

      /* Las medidas de referencia: [{ a, b, cm, peso }], extremos en px de
         la foto. El cuadro, si está colgado, se queda en el mismo sitio de
         la foto. Devuelve la escala de cada una en el mismo orden (null si
         no vale): si dos medidas no dan la misma, no cuadran entre sí. */
      reglas: function (lista) {
        lista = lista || [];
        var bajo = api.anclaPx();
        E.reglas = lista.filter(function (r) { return r && r.a && r.b && r.cm > 0; });
        calibrar();
        if (bajo) api.anclar(bajo);
        var n = ejes().n;
        return lista.map(function (r) { return r && r.a && r.b && r.cm > 0 ? escalaDe(r, n) : null; });
      },

      pared: function (o) {
        var bajo = api.anclaPx();
        if (o.yaw != null) E.yaw = o.yaw;
        if (o.pitch != null) E.pitch = o.pitch;
        if (o.roll != null) E.roll = o.roll;
        calibrar();
        if (bajo) api.anclar(bajo);
      },

      /* Perspectiva automática: busca las rectas de la foto dentro de `zona`
         ([x0,y0,x1,y1] en px, la pared alrededor del hueco) y saca de dónde
         fugan. Devuelve {yaw, pitch, roll, confianza} o null. No aplica nada:
         eso lo decide quien llama. */
      detectarPerspectiva: function (zona) {
        if (!E.fotoCanvas) return null;
        prepararBordes();
        var z = zona || [0, 0, E.W, E.H];
        var dentro = function (s) {
          var mx = (s.x1 + s.x2) / 2, my = (s.y1 + s.y2) / 2;
          return mx >= z[0] && mx <= z[2] && my >= z[1] && my <= z[3];
        };
        var hor = E.segmentos.filter(function (s) { return dentro(s) && Math.abs(s.ang) < 35; });
        /* Las verticales valen de toda la foto: son las mismas para todas
           las paredes (si la cámara no está ladeada). */
        var ver = E.segmentos.filter(function (s) { return Math.abs(Math.abs(s.ang) - 90) < 20; });
        var fx = fuga(hor), fy = fuga(ver);
        var peso = function (f) { return f ? f.apoyo : 0; };
        /* Hacen falta al menos ~2 rectas buenas de la pared para creerse la
           horizontal; si no, se deja de frente. */
        var vx = peso(fx) >= 120 && fx.n >= 2 ? fx.v : null;
        var vy = peso(fy) >= 200 && fy.n >= 3 ? fy.v : null;
        if (!vx && !vy) return null;
        var g = orientar(vx, vy);
        if (!g) return null;
        g.confianza = Math.min(1, peso(fx) / 600);
        return g;
      },

      /* Orientación a partir de 4 esquinas de algo rectangular de la pared
         (una tele, una ventana), en px y en orden: arriba-izq, arriba-der,
         abajo-der, abajo-izq. */
      orientarCon4: function (q) {
        function h(p) { return [p[0], p[1], 1]; }
        function linea(a, b) { return cruz(h(a), h(b)); }
        var vx = cruz(linea(q[0], q[1]), linea(q[3], q[2]));
        var vy = cruz(linea(q[0], q[3]), linea(q[1], q[2]));
        return orientar(vx, vy);
      },

      /* Las 4 esquinas en px de un rectángulo de la pared (cm) centrado en
         el píxel `p`: para dibujar la plantilla del ajuste a mano. */
      rectanguloEnPared: function (p, wcm, hcm) {
        var ej = ejes();
        var C = enPared(p, E.k, ej.n);
        if (!C) return null;
        return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(function (s) {
          return proyectar(sum(sum(C, esc(ej.e1, (s[0] * wcm) / 2)), esc(ej.e2, (s[1] * hcm) / 2)));
        });
      },

      /* La luz que viene de la IA ("desde la izquierda") como punto de
         partida; lo medido en la foto la corrige. */
      luzPrevia: function (desde) {
        previa = { izquierda: [-0.8, -0.6], derecha: [0.8, -0.6], arriba: [0, -1], frente: [0, -0.7] }[desde] || [0, -1];
      },

      obra: function (img) {
        E.obraImg = img;
        E.obraAspecto = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
        if (gl) {
          if (tex.obra) gl.deleteTexture(tex.obra);
          tex.obra = textura(gl, img, true);
        }
      },

      tamano: function (w, h) { E.w = w; E.h = h; },
      medidas: function () { return { w: E.w, h: E.h }; },

      /* Ancla = centro del borde inferior, en píxeles de la foto. */
      anclar: function (p) {
        var X = enPared(p, E.k, ejes().n);
        if (X) E.ancla = X;
      },
      anclaPx: function () { return E.ancla ? proyectar(E.ancla) : null; },

      /* Mueve el cuadro en centímetros sobre la pared (flechas del teclado). */
      moverCm: function (ds, dt) {
        if (!E.ancla) return;
        var ej = ejes();
        E.ancla = sum(sum(E.ancla, esc(ej.e1, ds)), esc(ej.e2, dt));
      },

      /* Punto de la pared (px) → cm sobre la vertical respecto a otro punto:
         para decir "a 22 cm del sofá". Positivo si el cuadro queda encima. */
      alturaSobre: function (pPx) {
        if (!E.ancla) return null;
        var X = enPared(pPx, E.k, ejes().n);
        if (!X) return null;
        return pto(res(X, E.ancla), ejes().e2);
      },
      /* Distancia horizontal en cm entre el centro del cuadro y un punto. */
      desvioHorizontal: function (pPx) {
        if (!E.ancla) return null;
        var X = enPared(pPx, E.k, ejes().n);
        if (!X) return null;
        return pto(res(E.ancla, X), ejes().e1);
      },
      /* Medida en cm entre dos píxeles, sobre la pared. */
      cmEntre: function (a, b) {
        var n = ejes().n;
        var A = enPared(a, E.k, n), B = enPared(b, E.k, n);
        return A && B ? largo(res(A, B)) : null;
      },
      /* De un píxel a otro, sobre la pared: [cm a lo ancho, cm hacia abajo]. */
      entre: function (a, b) {
        var ej = ejes();
        var A = enPared(a, E.k, ej.n), B = enPared(b, E.k, ej.n);
        if (!A || !B) return null;
        var d = res(B, A);
        return [pto(d, ej.e1), pto(d, ej.e2)];
      },
      /* El píxel del punto de la pared que queda a `ds` cm a lo ancho y `dt`
         hacia abajo del de `p`. Para dibujar encima sin mover el cuadro. */
      desplazarPx: function (p, ds, dt) {
        var ej = ejes();
        var X = enPared(p, E.k, ej.n);
        if (!X) return null;
        return proyectar(sum(sum(X, esc(ej.e1, ds)), esc(ej.e2, dt)));
      },

      /* El techo: la recta casi horizontal y larga más alta de la foto que
         pasa por la columna `x`, por encima de `yMax`. En casi todas las
         fotos que lo enseñan es la junta de la pared con el techo; si el
         techo no sale, no hay tal recta y devuelve null. Es una sugerencia:
         la página la enseña y el cliente la corrige. */
      detectarTecho: function (x, yMax) {
        if (!E.fotoCanvas) return null;
        prepararBordes();
        var mejor = null;
        E.segmentos.forEach(function (s) {
          if (Math.abs(s.ang) > 20 || s.largo < E.W * 0.3) return;
          if (x < Math.min(s.x1, s.x2) || x > Math.max(s.x1, s.x2)) return;
          var y = s.y1 + ((s.y2 - s.y1) * (x - s.x1)) / ((s.x2 - s.x1) || 1);
          if (y > yMax || y > E.H * 0.5 || y < E.H * 0.01) return;
          if (mejor == null || y < mejor) mejor = y;
        });
        return mejor;
      },

      /* La foto a la resolución de trabajo (px de la foto = px de este
         lienzo): para la lupa de las medidas. */
      fotoLienzo: function () { return E.fotoCanvas || null; },

      esquinas: function () { return E.ancla ? esquinas() : null; },

      contiene: function (p) {
        var q = api.esquinas();
        if (!q) return false;
        var dentro = false;
        for (var i = 0, j = 3; i < 4; j = i++) {
          if ((q[i][1] > p[1]) !== (q[j][1] > p[1]) &&
            p[0] < ((q[j][0] - q[i][0]) * (p[1] - q[i][1])) / (q[j][1] - q[i][1]) + q[i][0]) dentro = !dentro;
        }
        return dentro;
      },

      ajustes: function (o) {
        ['luz', 'sombra', 'despegue', 'oculta'].forEach(function (k) { if (o[k] != null) E[k] = o[k]; });
      },
      estado: function () { return { yaw: E.yaw, pitch: E.pitch, roll: E.roll, luz: E.luz, sombra: E.sombra }; },
      segmentos: function () { return E.segmentos; },
      ocupacion: function () { return E.ancla ? ocupacion() : null; },

      /* Sube el cuadro (de 3 en 3 cm, hasta metro y medio) hasta el primer
         sitio donde detrás solo hay pared: si al colgarlo sobre el mueble
         tapa una tele o una estantería, se va encima. Si no encuentra hueco
         antes de salirse de la foto, lo deja donde estaba. `maxSubida` (cm),
         si se sabe dónde está el techo: más arriba no cabe. */
      buscarHueco: function (maxSubida) {
        if (!E.ancla) return 0;
        var libre = 0.012;
        if (ocupacion() <= libre) return 0;
        var tope = maxSubida == null ? 150 : Math.min(150, maxSubida);
        var inicio = E.ancla.slice();
        var ej = ejes();
        for (var sub = 3; sub <= tope; sub += 3) {
          E.ancla = sum(inicio, esc(ej.e2, -sub));
          var q = esquinas();
          if (Math.min(q[0][1], q[1][1]) < E.H * 0.02) break;
          if (ocupacion() <= libre) {
            /* un poco de aire por encima de lo que había debajo */
            var aire = Math.min(sub + 12, tope);
            E.ancla = sum(inicio, esc(ej.e2, -aire));
            var q2 = esquinas();
            if (Math.min(q2[0][1], q2[1][1]) < E.H * 0.02) E.ancla = sum(inicio, esc(ej.e2, -sub));
            return sub;
          }
        }
        E.ancla = inicio;
        return 0;
      },

      render: render,

      /* La foto del cliente tal cual (reducida a la resolución de trabajo),
         para guardarla en Mi casa y poder reabrir la escena. */
      fotoBlob: function () {
        return new Promise(function (ok, mal) {
          if (!E.fotoCanvas) { mal(new Error('sin foto')); return; }
          E.fotoCanvas.toBlob(function (b) { b ? ok(b) : mal(new Error('foto')); }, 'image/jpeg', 0.9);
        });
      },

      /* `anchoMax` opcional: para la miniatura de Mi casa no hace falta la
         foto entera. */
      exportar: function (marca, anchoMax) {
        render();
        var s = anchoMax ? Math.min(1, anchoMax / E.W) : 1;
        var c = document.createElement('canvas');
        c.width = Math.round(E.W * s); c.height = Math.round(E.H * s);
        var x = c.getContext('2d');
        x.drawImage(canvas, 0, 0, c.width, c.height);
        if (s < 1) marca = '';
        if (marca) {
          var t = Math.max(12, Math.round(E.W * 0.018));
          x.font = '500 ' + t + 'px "Work Sans", system-ui, sans-serif';
          x.textAlign = 'right';
          x.textBaseline = 'bottom';
          x.fillStyle = 'rgba(0,0,0,.28)';
          x.fillText(marca, E.W - t + 1, E.H - t * 0.8 + 1);
          x.fillStyle = 'rgba(255,255,255,.85)';
          x.fillText(marca, E.W - t, E.H - t * 0.8);
        }
        return new Promise(function (ok, mal) {
          c.toBlob(function (b) { b ? ok(b) : mal(new Error('exportar')); }, 'image/jpeg', 0.9);
        });
      },
    };
    return api;
  }

  window.CsSalonMotor = { crear: crear };
})();
